import { Injectable, inject } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { NotificationService } from '../../notification-service/notification.service';
import { GameProductionState } from '../../model/GameProductionState';
import { EditorGameDefinitionService } from '../editor-game-definition.service';
import { PluginType } from '@meta-press/shared-plugins';
import { env } from 'process';

// Language enum with unique keys for each supported language
export enum SupportedLanguage {
	FINNISH = 'FINNISH',
	ENGLISH = 'ENGLISH',
	SWEDISH = 'SWEDISH',
}

// Language definition interface
export interface LanguageDefinition {
	id: SupportedLanguage;
	shortCode: string;
	displayName: string;
	llmPrompt: string;
}

// Difficulty enum with unique keys for each supported difficulty level
export enum SupportedDifficulty {
	EASY = 'EASY',
	MEDIUM = 'MEDIUM',
	HARD = 'HARD',
}

// Difficulty definition interface
export interface DifficultyDefinition {
	id: SupportedDifficulty;
	displayName: string;
	llmPrompt: string;
}

@Injectable({
	providedIn: 'root',
})
export class SettingsService {
	private readonly appVersion: string;
	private readonly appVersionPostfix: string;
	private readonly appDataVersion: number;
	private buildVersion: number | null = null;
	private generationLanguage = 'Suomi'; // Default language is Finnish
	private generationDifficulty = 'Keskivaikea'; // Default difficulty is Medium

	// Environment flags for plugin visibility
	private readonly showBetaPlugins: boolean;
	private readonly showInternalPlugins: boolean;
	private readonly showAlphaPlugins: boolean;
	private readonly showProductionPlugins: boolean;
	private readonly showPrototypePlugins: boolean;
	private readonly isProduction: boolean;

	// Define all supported languages with their properties
	private readonly languages: LanguageDefinition[] = [
		{
			id: SupportedLanguage.FINNISH,
			shortCode: 'fi',
			displayName: 'Suomi',
			llmPrompt:
				'On todella tärkeää, että kaiken materiaalin kieli on: suomi. Et saa luoda tuloksia muulla kielellä.',
		},
		{
			id: SupportedLanguage.ENGLISH,
			shortCode: 'en',
			displayName: 'English',
			llmPrompt:
				'On todella tärkeää, että kaiken materiaalin kieli on: englanti (English). Et saa luoda tuloksia muulla kielellä.',
		},
		{
			id: SupportedLanguage.SWEDISH,
			shortCode: 'sv',
			displayName: 'Svenska',
			llmPrompt:
				'On todella tärkeää, että kaiken materiaalin kieli on: ruotsi (svenska). Et saa luoda tuloksia muulla kielellä.',
		},
	];

	// Define all supported difficulty levels with their properties
	private readonly difficulties: DifficultyDefinition[] = [
		{
			id: SupportedDifficulty.EASY,
			displayName: 'Helppo',
			llmPrompt:
				'Luo helppo versio. Käytä yksinkertaista kieltä ja vähemmän monimutkaisia kysymyksiä.',
		},
		{
			id: SupportedDifficulty.MEDIUM,
			displayName: 'Keskivaikea',
			llmPrompt:
				'Luo keskivaikea versio. Käytä normaalia kieltä ja tasapainoista vaikeutta.',
		},
		{
			id: SupportedDifficulty.HARD,
			displayName: 'Vaikea',
			llmPrompt:
				'Luo haastava versio. Käytä monipuolisempaa kieltä, ja vaikeita kysymyksiä. Jos lähdemateriaalissa on vaikeita sanoja, voit ottaa myös niitä mukaan sellaisenaan.',
		},
	];

	// Inject EditorGameDefinitionService for plugin filtering
	private editorGameDefinitionService = inject(EditorGameDefinitionService);

	constructor(private notificationService: NotificationService) {
		this.appVersion = environment.appVersion;
		this.appVersionPostfix = environment.appVersionPostfix;
		this.appDataVersion = environment.appDataVersion;
		this.showBetaPlugins = environment.showBetaPlugins;
		this.showInternalPlugins = environment.showInternalPlugins;
		this.showProductionPlugins = environment.showProductionPlugins;
		this.showAlphaPlugins = environment.showAlphaPlugins;
		this.showBetaPlugins = environment.showBetaPlugins;
		this.isProduction = environment.production;
		this.showPrototypePlugins = environment.showPrototypePlugins;
	}

	public get AppVersion(): string {
		return this.appVersion;
	}

	public get AppVersionPostfix(): string {
		return this.appVersionPostfix;
	}

	public get AppVersionWithPostfix(): string {
		return `${this.appVersion} ${this.appVersionPostfix}`;
	}

	public get BuildVersion(): number {
		if (this.buildVersion === null) {
			try {
				// Extract the last digit/number from app version (e.g., "1.2.3" -> 3)
				const versionParts = this.appVersion.split('.');
				if (versionParts.length === 0) {
					throw new Error('Invalid app version format');
				}

				const lastPart = versionParts[versionParts.length - 1];

				// Validate that the last part is a valid number
				if (!/^\d+$/.test(lastPart)) {
					throw new Error('Last version part is not a valid number');
				}

				this.buildVersion = parseInt(lastPart, 10);
			} catch (error) {
				const errorMessage = `Failed to extract build version from app version '${this.appVersion}': ${error}`;
				this.notificationService.ShowToast(errorMessage, 'danger');
				throw new Error(errorMessage);
			}
		}

		return this.buildVersion;
	}

	public get AppDataVersion(): number {
		return this.appDataVersion;
	}

	public get GenerationLanguage(): string {
		return this.generationLanguage;
	}

	public set GenerationLanguage(language: string) {
		this.generationLanguage = language;
	}

	public get GenerationDifficulty(): string {
		return this.generationDifficulty;
	}

	public set GenerationDifficulty(difficulty: string) {
		this.generationDifficulty = difficulty;
	}

	// Get all available languages
	public get AvailableLanguages(): LanguageDefinition[] {
		return this.languages;
	}

	// Get all available difficulty levels
	public get AvailableDifficulties(): DifficultyDefinition[] {
		return this.difficulties;
	}

	// Get current language definition
	public GetCurrentLanguageDefinition(): LanguageDefinition | undefined {
		return this.languages.find(
			(lang) => lang.displayName === this.generationLanguage,
		);
	}

	// Get current difficulty definition
	public GetCurrentDifficultyDefinition(): DifficultyDefinition | undefined {
		return this.difficulties.find(
			(diff) => diff.displayName === this.generationDifficulty,
		);
	}

	// Returns a language prompt for LLM that specifies which language to generate content in
	public GetTargetLanguagePrompt(): string {
		const currentLang = this.GetCurrentLanguageDefinition();
		return currentLang
			? currentLang.llmPrompt
			: this.languages[0].llmPrompt; // Default to Finnish
	}

	// Returns a difficulty prompt for LLM that specifies which difficulty level to generate content for
	public GetTargetDifficultyPrompt(): string {
		const currentDiff = this.GetCurrentDifficultyDefinition();
		return currentDiff
			? currentDiff.llmPrompt
			: this.difficulties[1].llmPrompt; // Default to Medium
	}

	/**
	 * Get the production states that should be shown based on environment settings
	 * - Production only by default
	 * - Production + Beta if showBetaPlugins is enabled
	 * - All states if showInternalPlugins is enabled
	 */
	public getAvailableProductionStates(): GameProductionState[] {
		const gamesToShow = [];

		if (this.showProductionPlugins)
			gamesToShow.push(GameProductionState.Production);

		if (this.showBetaPlugins) gamesToShow.push(GameProductionState.Beta);

		if (this.showAlphaPlugins) gamesToShow.push(GameProductionState.Alpha);

		if (this.showPrototypePlugins)
			gamesToShow.push(GameProductionState.Prototype);

		return gamesToShow;
	}

	/**
	 * Get filtered and sorted plugin types based on environment settings
	 * - Filters by production state (Production, Beta, or Internal)
	 * - Filters out internal plugins if not in development mode
	 * - Sorts alphabetically by title (Finnish locale)
	 */
	public getFilteredPluginTypes(): Array<{
		type: PluginType;
		title: string;
		description: string;
	}> {
		const allowedStates = this.getAvailableProductionStates();

		// Get all editor game definitions and filter by state and internal flag
		const filteredGames =
			this.editorGameDefinitionService.editorGameDefinitions.filter(
				(game) => {
					// Check if game's state is in allowed states
					const isAllowedState = allowedStates.includes(game.state);

					// Check if internal plugins should be shown
					const isAllowedInternal =
						!game.isInternal || this.showInternalPlugins;

					return isAllowedState && isAllowedInternal;
				},
			);

		// Map to simplified format and sort alphabetically by title
		return filteredGames
			.map((game) => ({
				type: game.type,
				title: game.title,
				description: game.description,
			}))
			.sort((a, b) => a.title.localeCompare(b.title, 'fi'));
	}
}
